import { useQuery, useQueryClient } from '@tanstack/react-query';
import { safeFetch } from '../utils/fetchJson';

export function useAnomalyData({ serverId, typeFilter, severityFilter, metricFilter, page, perPage, refreshKey, hours }) {
  const params = new URLSearchParams();
  if (serverId) params.set('server_id', serverId);
  if (typeFilter && typeFilter !== 'all') params.set('anomaly_type', typeFilter);
  if (severityFilter && severityFilter !== 'all') params.set('severity', severityFilter);
  if (metricFilter && metricFilter !== 'all') params.set('metric_name', metricFilter);
  if (hours) params.set('from_ts', new Date(Date.now() - hours * 3600 * 1000).toISOString());
  params.set('limit', String(perPage));
  params.set('offset', String((page - 1) * perPage));

  const { data: anomaliesData, isLoading: loading } = useQuery({
    queryKey: ['anomalies-list', serverId, typeFilter, severityFilter, metricFilter, page, perPage, refreshKey, hours],
    queryFn: async () => {
      const res = await fetch(`/api/v1/anomalies?${params.toString()}`);
      const data = await res.json();
      return { anomalies: data.items || [], total: data.total || 0 };
    },
    refetchInterval: 60000,
    enabled: !!serverId || !serverId,
  });

  const { data: statsData } = useQuery({
    queryKey: ['anomalies-stats', serverId, refreshKey],
    queryFn: async () => {
      const sParams = new URLSearchParams();
      if (serverId) sParams.set('server_id', serverId);
      const res = await fetch(`/api/v1/anomalies/stats?${sParams.toString()}`);
      return await res.json();
    },
    refetchInterval: 120000,
    enabled: true,
  });

  const { data: detectorStatus } = useQuery({
    queryKey: ['anomaly-detector-status', refreshKey],
    queryFn: async () => {
      const res = await fetch('/api/v1/anomalies/detector/status');
      if (!res.ok) return null;
      return await res.json();
    },
    refetchInterval: 30000,
    enabled: true,
  });

  return { anomaliesData, loading, statsData, detectorStatus };
}

export function useAnomalyConfig(serverId) {
  const queryClient = useQueryClient();

  const { data: configs, isLoading } = useQuery({
    queryKey: ['anomaly-config', serverId],
    queryFn: async () => {
      const sParams = serverId ? `?server_id=${serverId}` : '';
      const res = await fetch(`/api/v1/anomalies/config${sParams}`);
      return await res.json();
    },
    refetchInterval: 120000,
  });

  const setConfig = async (body) => {
    const res = await fetch('/api/v1/anomalies/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    queryClient.invalidateQueries({ queryKey: ['anomaly-config'] });
    return await res.json();
  };

  const deleteConfig = async (configId) => {
    await fetch(`/api/v1/anomalies/config/${configId}`, { method: 'DELETE' });
    queryClient.invalidateQueries({ queryKey: ['anomaly-config'] });
  };

  return { configs, isLoading, setConfig, deleteConfig };
}

export function useAnomalyOverview(hours, refreshKey) {
  return useQuery({
    queryKey: ['anomaly-overview', hours, refreshKey],
    queryFn: async () => {
      const data = await safeFetch(`/api/v1/anomalies/overview?hours=${hours}`);
      return data || {};
    },
    refetchInterval: 120000,
  });
}

export function useAnomalyTimeline(hours, typeFilter, severityFilter, refreshKey) {
  return useQuery({
    queryKey: ['anomaly-timeline', hours, typeFilter, severityFilter, refreshKey],
    queryFn: async () => {
      const params = new URLSearchParams({ hours: String(hours) });
      if (typeFilter && typeFilter !== 'all') params.set('anomaly_type', typeFilter);
      if (severityFilter && severityFilter !== 'all') params.set('severity', severityFilter);
      const data = await safeFetch(`/api/v1/anomalies/timeline?${params.toString()}`);
      return data || [];
    },
    refetchInterval: 120000,
  });
}

export function useAnomalyByServer(hours, refreshKey) {
  return useQuery({
    queryKey: ['anomaly-by-server', hours, refreshKey],
    queryFn: async () => {
      const data = await safeFetch(`/api/v1/anomalies/by-server?hours=${hours}`);
      return data || [];
    },
    refetchInterval: 300000,
  });
}

export function useAnomalyWazuh(hours, refreshKey) {
  return useQuery({
    queryKey: ['anomaly-wazuh-recent', hours, refreshKey],
    queryFn: async () => {
      const data = await safeFetch(`/api/v1/anomalies/wazuh/recent?hours=${hours}`);
      return data || [];
    },
    refetchInterval: 120000,
  });
}

export function useAnomalyContext(anomalyId) {
  return useQuery({
    queryKey: ['anomaly-context', anomalyId],
    queryFn: async () => {
      const data = await safeFetch(`/api/v1/anomalies/${anomalyId}/context`);
      return data || {};
    },
    enabled: !!anomalyId,
    staleTime: 60000,
    retry: 1,
  });
}

export function useDismissAnomaly() {
  const queryClient = useQueryClient();
  return async (anomalyId, body) => {
    const res = await fetch(`/api/v1/anomalies/${anomalyId}/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) throw new Error(`Dismiss failed (${res.status})`);
    queryClient.invalidateQueries({ queryKey: ['anomalies-list'] });
    queryClient.invalidateQueries({ queryKey: ['anomaly-overview'] });
    queryClient.invalidateQueries({ queryKey: ['anomaly-timeline'] });
    queryClient.invalidateQueries({ queryKey: ['anomaly-by-server'] });
    return await res.json();
  };
}
